/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { INodeProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IAddressAddItem, IAddressError } from '@msdyn365-commerce-modules/address';
import { IAddressShowItem } from '@msdyn365-commerce-modules/address';
import { ICheckoutBillingAddressViewProps } from './gdn-checkout-billing-address';
import { billingAddressOption } from './gdn-checkout-billing-address.props.autogenerated';

export interface IAddressShowProps {
    AddressDetail: INodeProps;
    items: IAddressShowItem[];
    addressBillingAddressHeading: string;
    selectedBillingAddressOption: string;
}

export interface IAddressAddUpdateProps {
    AddressForm: INodeProps;
    heading: React.ReactNode;
    items: IAddressAddItem[];
    hasError: boolean;
    error: IAddressError;
    isShowSaveButton: boolean;
    saveButton: React.ReactNode;
    isShowCancelButton: boolean;
    cancelButton: React.ReactNode;
    addressBillingAddressHeading: string;
    selectedBillingAddressOption: string;
}

enum billingAddressOptions {
    sameAsShipping = 'Same as Shipping Address',
    primaryAccount = 'Primary Account Address',
    newAddress = 'New Address'
}

/**
 * Address show component.
 * @param param0 - Root param.
 * @param param0.AddressDetail - Address detail.
 * @param param0.items - IAddressShowItem[].
 * @param param0.addressBillingAddressHeading - The heading text for the billing address section.
 * @param param0.selectedBillingAddressOption - The selected billing address option, which must be a key of billingAddressOptions.
 * @returns - Address Node.
 */
const AddressShow: React.FC<IAddressShowProps> = ({ AddressDetail, items, addressBillingAddressHeading, selectedBillingAddressOption }) => {
    return (
        <Node {...AddressDetail}>
            <p className='msc-address-detail__billing-heading'>
                {addressBillingAddressHeading} ({billingAddressOptions[selectedBillingAddressOption as keyof typeof billingAddressOptions]})
            </p>
            {items.map((item: IAddressShowItem) => {
                return <>{item.description}</>;
            })}
        </Node>
    );
};

/**
 * Address Add Update Component.
 * @param param0 - Root param.
 * @param param0.AddressForm - Address form.
 * @param param0.heading - Address Heading.
 * @param param0.items - IAddressAddItem[].
 * @param param0.hasError - Boolean.
 * @param param0.error - IAddressError.
 * @param param0.isShowSaveButton - Boolean.
 * @param param0.saveButton - Save button.
 * @param param0.isShowCancelButton - Boolean.
 * @param param0.cancelButton - Cancel button.
 * @returns - AddressForm Node.
 */
const AddressAddUpdate: React.FC<IAddressAddUpdateProps> = ({
    AddressForm,
    heading,
    items,
    hasError,
    error,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton,
    selectedBillingAddressOption,
    addressBillingAddressHeading
}) => {
    return (
        <Node {...AddressForm}>
            <p className='msc-address-form__billing-heading'>
                {' '}
                {addressBillingAddressHeading} ({billingAddressOptions[selectedBillingAddressOption as keyof typeof billingAddressOptions]})
            </p>
            {heading}
            {items.map((item: IAddressAddItem) => {
                const { AddressItem, key, label, alert, input } = item;
                return (
                    <Node {...AddressItem} key={key}>
                        {label}
                        {alert}
                        {input}
                    </Node>
                );
            })}
            {hasError && (
                <Node {...error.AddressError}>
                    {error.title}
                    {error.message}
                </Node>
            )}
            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

/**
 * Checkout Billing Address View Component.
 * @param props - Props.
 * @returns - CheckoutBillingAddress Module.
 */
const CheckoutBillingAddressView: React.FC<ICheckoutBillingAddressViewProps> = props => {
    const {
        CheckoutBillingAddress,
        checkoutErrorRef,
        alert,
        viewState,
        // heading,
        sameAsShippingRadioButton,
        showAddress,
        showAddOrUpdateAddress,
        newAddressRadioButton,
        primaryAccountAddressRadioButton,
        resources,
        selectedBillingAddressOption,
        config
    } = props;
    const { addressBillingAddressHeading } = resources;

    const customShowAddressProps = {
        ...showAddress,
        addressBillingAddressHeading,
        selectedBillingAddressOption
    };

    const customUpdateAddressProps = {
        ...showAddOrUpdateAddress,
        addressBillingAddressHeading,
        selectedBillingAddressOption
    };

    const radioButtons = [
        { option: billingAddressOption.sameAsShipping, component: sameAsShippingRadioButton },
        { option: billingAddressOption.primaryAccount, component: primaryAccountAddressRadioButton },
        { option: billingAddressOption.newAddress, component: newAddressRadioButton }
    ];

    const orderedRadioButtons = radioButtons
        .sort((a, b) => {
            if (a.option === config.billingAddressOption) {
                return -1;
            }
            if (b.option === config.billingAddressOption) {
                return 1;
            }
            return 0;
        })
        .map(button => button.component);

    return (
        <Module {...CheckoutBillingAddress} ref={checkoutErrorRef}>
            {alert}
            {viewState.isShowRadioButtons && orderedRadioButtons}
            {viewState.isShowAddress && <AddressShow {...customShowAddressProps} />}
            {viewState.isShowAddOrUpdateAddress && <AddressAddUpdate {...customUpdateAddressProps} />}
        </Module>
    );
};

export default CheckoutBillingAddressView;
